// Copyright (c) 2021 WiseTime. All rights reserved.

import React from "react"
import { Trans } from "@lingui/react"
import { useDispatch, useSelector } from "react-redux"
import { useKeyPressEvent } from "react-use"
import styled from "styled-components/macro"
import { ActionPaneButtonsWrapper, PrimaryRevertedButton } from "features/shared/styles"
import * as TimelineSlice from "../slices/timeline"
import * as TimelineActions from "../actions"
import * as UserContextSlice from "features/user-context/slice"
import { narrativeType } from "../timeGroups"
import AddToEntryDropdown from "./AddToEntryDropdown"
import { CreateTimeGroupRequest, TimeRow } from "generated/wisetime/user/timeline/v4/timeline_pb"
import { groupTimeRowsByTags } from "../timeRows"
import { IdsList } from "../../tags/tags"
import { isDisabledShortcut } from "../../../lib/utils"
import { DirectTimelinePostPreference } from "../../../generated/wisetime/type/site_preferences_pb"
import PostActions from "../shared/PostActions"
import { shortcuts } from "../../../app/shortcuts"

const StyledActionPaneButtonsWrapper = styled(ActionPaneButtonsWrapper)`
  margin: 0;

  > * {
    &:not(:last-child) {
      margin-right: 2rem;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.screen.sm}) {
    padding-bottom: .5rem;
  }
`

const StyledPrimaryRevertedButton = styled(PrimaryRevertedButton)`
  padding-left: 1rem;
  padding-right: 1rem;
`

const Wrapper = styled.div`
  padding-top: 1rem;
  border-top: 1px solid ${({ theme }) => theme.colors.mercury};
`

const Heading = styled.p`
  line-height: normal;
  font-weight: 600;
  padding: 0 2.2rem;
  margin-bottom: 7px;
`

const QuickPostActionsWrapper = styled.div`
  padding: 2rem;
  border-top: 1px solid ${({ theme }) => theme.colors.mercury};

  @media (max-width: ${({ theme }) => theme.screen.sm}) {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }
`

type Props = {
  timeRows: TimeRow.AsObject[],
  hasTagWithDifferentAssignee: boolean,
}

const TimeRowsActionPaneButtons = ({
  timeRows,
  hasTagWithDifferentAssignee,
}: Props): React.ReactElement<Props> | null => {
  if (timeRows.length === 0) {
    return null
  }

  const dispatch = useDispatch()
  const currentSite = useSelector(UserContextSlice.selectCurrentSite)
  const currentSiteId = useSelector(UserContextSlice.selectCurrentSiteId)
  const enableDirectPostFromTimeline = currentSite?.postTimePrefs?.directTimelinePostPreference === DirectTimelinePostPreference.DIRECT_TIMELINE_POST_ENABLED
  const timeRowIdsInGroupingProgress = useSelector(TimelineSlice.selectTimeRowIdsInGroupingProgress)
  const hasTimeRowsInGroupingProgress = timeRowIdsInGroupingProgress.some(inProgress => timeRows.map(r=> r.id).includes(inProgress))

  const groupedTimeRowsByTags = groupTimeRowsByTags(timeRows, currentSiteId)
  const groupKeys = Object.keys(groupedTimeRowsByTags)
  const isSingleGroup = groupKeys.length === 1 || (groupKeys.length === 2 && groupKeys.some(k => k === ""))

  const createGroup = (): void => {
    if (hasTimeRowsInGroupingProgress) {
      return
    }
    const request = new CreateTimeGroupRequest()
      .setTimeRowIdsList(timeRows.map(r => r.id))
      .setNarrativeType(narrativeType(currentSite))
    dispatch(TimelineActions.createTimeGroup({ request }))
    dispatch(TimelineSlice.addTimeRowIdsInGroupingProgress(request.getTimeRowIdsList()))
  }

  const createMultipleGroups = (post: boolean): void => {
    if (hasTimeRowsInGroupingProgress) {
      return
    }
    Object.keys(groupedTimeRowsByTags).forEach(key => {
      const timeRowIdsList: IdsList | undefined = groupedTimeRowsByTags[key]?.map(r => r.id) ?? []
      if (timeRowIdsList.length !== 0) {
        const request = new CreateTimeGroupRequest()
          .setTimeRowIdsList(timeRowIdsList)
          .setNarrativeType(narrativeType(currentSite))
        dispatch(TimelineActions.createTimeGroup({
          request,
          skipSelection: true,
          post,
        }))
        dispatch(TimelineSlice.addTimeRowIdsInGroupingProgress(request.getTimeRowIdsList()))
      }
    })
  }

  const newEntry = () => {
    if (isSingleGroup) {
      createGroup()
    } else {
      createMultipleGroups(false)
    }
  }

  useKeyPressEvent(shortcuts.timeGroup, () => {
    if (isDisabledShortcut()) {
      return
    }
    newEntry()
  })

  return (
    <Wrapper>
      <Heading>
        <Trans id="label_timesheet" message="Timesheet"/>
      </Heading>
      <StyledActionPaneButtonsWrapper>
        <AddToEntryDropdown timeRows={timeRows}/>
        <StyledPrimaryRevertedButton
          type="button"
          onClick={newEntry}
        >
          {isSingleGroup
            ? <Trans id="label_new_entry" message="New Entry"/>
            : <Trans id="label_new_entries" message="New Entries"/>
          }
          {` (${shortcuts.timeGroup})`}
        </StyledPrimaryRevertedButton>
      </StyledActionPaneButtonsWrapper>
      {enableDirectPostFromTimeline && (
        <>
          <QuickPostActionsWrapper>
            <PostActions
              timeRows={timeRows}
              createAndPostTimeGroups={() => createMultipleGroups(true)}
              hasTagWithDifferentAssignee={hasTagWithDifferentAssignee}
            />
          </QuickPostActionsWrapper>
        </>
      )}
    </Wrapper>
  )
}

export default TimeRowsActionPaneButtons
